import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RULE_INPUTS, RULE_ACTIONS } from "@/components/RuleBuilder";
import DiffPanel from "./DiffPanel";
import {
  Sparkles,
  Plus,
  Share2,
  Trash2,
  TrendingUp,
  Copy,
  Check,
  Store,
  User,
  Zap,
  GitFork,
  Tag,
  Search,
  History,
  Clock,
  Flame,
  FlaskConical,
} from "lucide-react";
import { formatApiError } from "@/lib/format";
import { toast } from "sonner";

// Compact envs for the per-card live simulator — cycles through 5 states.
const SIM_ENVS = [
  { weather: "rain", time: "morning", event: "none", inventory: "available", venue: "café" },
  { weather: "sunny", time: "lunch", event: "concert", inventory: "available", venue: "mall" },
  { weather: "cloudy", time: "evening", event: "football", inventory: "low", venue: "gym" },
  { weather: "sunny", time: "late-night", event: "none", inventory: "out_of_stock", venue: "airport" },
  { weather: "snow", time: "morning", event: "festival", inventory: "available", venue: "café" },
];

// Public list of categories with a short display label + accent colour.
const CATEGORY_META = {
  retail: { label: "Retail", color: "#ff6b6b" },
  hospitality: { label: "Hospitality", color: "#E8D6A3" },
  transit: { label: "Transit", color: "#00E5FF" },
  entertainment: { label: "Entertainment", color: "#CCFF00" },
  general: { label: "General", color: "#a78bfa" },
};


/**
 * Highlight — case-insensitive substring highlighter. Wraps matches in
 * a <mark> with a soft gold background so buyers see WHY a card matched.
 */
function Highlight({ text = "", query = "" }) {
  const q = query.trim();
  if (!q) return text;
  const parts = text.split(new RegExp(`(${escapeRegExp(q)})`, "ig"));
  return parts.map((p, i) =>
    p.toLowerCase() === q.toLowerCase() ? (
      <mark
        key={i}
        data-testid="rt-search-mark"
        className="bg-[#D4B878]/40 text-[#E8D6A3] rounded px-0.5"
      >
        {p}
      </mark>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    ),
  );
}

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}


/**
 * /app/rules/marketplace
 * Advertisers can save any rule as a reusable template and browse a
 * curated marketplace of public templates (seeded + user-published).
 */

export default function RuleMarketplace() {
  const [tab, setTab] = useState("public"); // public | mine
  const [pub, setPub] = useState([]);
  const [mine, setMine] = useState([]);
  const [categories, setCategories] = useState([]);
  const [trending, setTrending] = useState([]);
  const [activeCat, setActiveCat] = useState(null); // null = all
  const [search, setSearch] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [geoCountries, setGeoCountries] = useState([]); // [{country, fires}]
  // country: "" = auto-detect · "GLOBAL" = user explicitly opted out of any
  // geo context · "IN"/"GB"/… = fixed country. Kept as three-way so the
  // Global option can actually strip fires_in_country chips even though the
  // backend defaults to auto-detect on an empty param.
  const [country, setCountry] = useState("");
  const [detectedCountry, setDetectedCountry] = useState(""); // echoed by API
  const navigate = useNavigate();

  const loadAll = async () => {
    try {
      const qs = country && country !== "GLOBAL"
        ? `?country=${encodeURIComponent(country)}`
        : "";
      const [p, m, c, tr, gc] = await Promise.all([
        api.get(`/ads/rule-templates/public${qs}`),
        api.get("/ads/rule-templates/mine"),
        api.get("/ads/rule-templates/categories"),
        api.get("/ads/rule-templates/trending?limit=6&window_days=7"),
        api.get("/ads/rule-templates/geo-countries?days=30"),
      ]);
      // GLOBAL mode: strip the fires_in_country field the backend attaches
      // via auto-detect so the UI chip disappears — the user opted out.
      const items = country === "GLOBAL"
        ? p.data.map(({ fires_in_country, ...rest }) => rest)
        : p.data;
      setPub(items);
      const cc = country === "GLOBAL"
        ? ""
        : (p.headers?.["x-detected-country"] || p.headers?.["X-Detected-Country"] || "");
      setDetectedCountry(cc);
      setMine(m.data);
      setCategories(c.data);
      setTrending(tr.data.items || []);
      setGeoCountries(gc.data.countries || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  const searchFilter = (items) => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(
      (t) =>
        (t.name || "").toLowerCase().includes(q) ||
        (t.description || "").toLowerCase().includes(q) ||
        (t.input || "").toLowerCase().includes(q) ||
        (t.value || "").toLowerCase().includes(q),
    );
  };

  const filteredPub = useMemo(
    () => searchFilter(activeCat ? pub.filter((t) => t.category === activeCat) : pub),
    [pub, activeCat, search],
  );
  const filteredMine = useMemo(
    () =>
      searchFilter(
        activeCat ? mine.filter((t) => (t.category || "general") === activeCat) : mine,
      ),
    [mine, activeCat, search],
  );
  const list = tab === "public" ? filteredPub : filteredMine;

  return (
    <div className="space-y-8" data-testid="rules-marketplace-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <StateBadge state="BETA" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
              Rule marketplace
            </span>
          </div>
          <h1 className="font-display font-black tracking-tighter text-4xl md:text-5xl">
            Reusable programmable logic.
          </h1>
          <p className="text-zinc-400 mt-2 max-w-lg">
            Save your best rules. Discover rules other advertisers use. One
            click adds a rule to any campaign.
          </p>
        </div>
        <NewTemplateDialog
          open={newOpen}
          setOpen={setNewOpen}
          onCreated={() => {
            setTab("mine");
            loadAll();
          }}
        />
      </div>

      <div className="flex bg-white/[0.02] border border-white/10 p-1 w-fit">
        {[
          { k: "public", label: "Marketplace", n: pub.length },
          { k: "mine", label: "My templates", n: mine.length },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`rt-tab-${t.k}`}
            className={`px-4 py-1.5 text-xs uppercase tracking-[0.18em] transition ${
              tab === t.k
                ? "bg-[#D4B878] text-black font-bold"
                : "text-white/50 hover:text-white"
            }`}
          >
            {t.label}
            <span className="ml-2 font-mono">{t.n}</span>
          </button>
        ))}
      </div>

      {/* Country geo filter — surfaces rules that have already fired in
          the user's region so agencies see local proof, not global fluff */}
      {tab === "public" && (
        <div
          data-testid="rule-marketplace-country-filter"
          className="flex items-center gap-3 flex-wrap"
        >
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/50">
            Fires in
          </span>
          <select
            data-testid="rule-marketplace-country-select"
            value={country || "__auto__"}
            onChange={(e) => {
              const v = e.target.value;
              setCountry(v === "__auto__" ? "" : v);
            }}
            className="bg-white/5 border border-white/10 rounded px-2 py-1 text-xs text-white font-mono"
          >
            <option value="__auto__">Auto ({detectedCountry || "global"})</option>
            <option value="GLOBAL">— Global · no filter —</option>
            {geoCountries.map((g) => (
              <option key={g.country} value={g.country}>
                {g.country} · {g.fires} fires (30d)
              </option>
            ))}
          </select>
          {(country !== "GLOBAL" && (country || detectedCountry)) && (
            <span
              data-testid="rule-marketplace-country-active"
              className="text-[10px] uppercase tracking-[0.22em] text-[#CCFF00] flex items-center gap-1"
            >
              <Flame size={11} /> Sorted by fires in{" "}
              <span className="font-mono">{country || detectedCountry}</span>
            </span>
          )}
        </div>
      )}

      {tab === "public" && trending.length > 0 && (
        <div
          data-testid="rt-trending-strip"
          className="border border-[#CCFF00]/20 bg-gradient-to-r from-[#CCFF00]/[0.05] via-[#D4B878]/[0.04] to-transparent rounded-lg p-4"
        >
          <div className="flex items-center gap-2 mb-3">
            <Flame size={13} className="text-[#CCFF00]" />
            <span className="text-[10px] uppercase tracking-[0.24em] text-[#CCFF00] font-semibold">
              Most forked this week
            </span>
            <span className="text-[10px] text-white/30 uppercase tracking-[0.18em]">
              · last 7 days
            </span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
            {trending.map((it, idx) => {
              const meta =
                CATEGORY_META[it.category] || CATEGORY_META.general;
              const label = it.version == null ? "tip" : `v${it.version}`;
              return (
                <button
                  key={`${it.template_id}-${it.version ?? "tip"}`}
                  data-testid={`rt-trending-${it.template_id}-${it.version ?? "tip"}`}
                  onClick={async () => {
                    try {
                      const r = await api.post(
                        `/ads/rule-templates/${it.template_id}/fork${
                          it.version != null ? `?version=${it.version}` : ""
                        }`,
                      );
                      toast.success(`Forked "${r.data.name}"`);
                      loadAll();
                    } catch (e) {
                      toast.error(formatApiError(e));
                    }
                  }}
                  className="group text-left snap-start shrink-0 w-64 p-3 bg-black/40 border border-white/10 hover:border-[#CCFF00]/50 rounded-md transition-all"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[9px] font-mono text-[#E8D6A3]">
                      #{idx + 1}
                    </span>
                    <span
                      className="text-[9px] uppercase tracking-[0.18em] font-semibold"
                      style={{ color: meta.color }}
                    >
                      {meta.label}
                    </span>
                  </div>
                  <div className="text-sm font-medium text-white truncate">
                    {it.name}
                  </div>
                  <div className="text-[10px] font-mono text-white/40 truncate mt-1">
                    when {it.input} → {it.action}
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 text-[10px] text-[#CCFF00] font-semibold">
                      <TrendingUp size={10} /> {it.forks}{" "}
                      {it.forks === 1 ? "fork" : "forks"}
                    </span>
                    <span className="text-[9px] uppercase tracking-[0.16em] text-white/40 font-mono">
                      {label}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-1 text-[10px] text-white/50 group-hover:text-[#CCFF00] transition-colors">
                    <GitFork size={10} /> Fork this snapshot
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 bg-white/[0.02] border border-white/10 px-3 py-1.5 max-w-md flex-1 min-w-[220px]">
          <Search size={13} className="text-white/40 shrink-0" />
          <input
            data-testid="rt-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, description, input…"
            className="bg-transparent border-none text-sm h-6 w-full text-white placeholder:text-white/30 focus:outline-none"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              data-testid="rt-search-clear"
              className="text-white/40 hover:text-white text-xs"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="rt-categories">
        <button
          onClick={() => setActiveCat(null)}
          data-testid="rt-cat-all"
          className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] border transition ${
            activeCat === null
              ? "bg-white text-black border-white font-bold"
              : "bg-transparent text-white/60 border-white/15 hover:border-white"
          }`}
        >
          All
        </button>
        {categories.map((c) => {
          const meta = CATEGORY_META[c.key] || CATEGORY_META.general;
          const on = activeCat === c.key;
          return (
            <button
              key={c.key}
              onClick={() => setActiveCat(c.key)}
              data-testid={`rt-cat-${c.key}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] border transition inline-flex items-center gap-1.5 ${
                on
                  ? "text-black font-bold"
                  : "text-white/60 border-white/15 hover:border-white"
              }`}
              style={
                on
                  ? { background: meta.color, borderColor: meta.color }
                  : {}
              }
            >
              <Tag size={10} style={on ? { color: "black" } : { color: meta.color }} />
              {meta.label}
              <span className={`ml-1 font-mono ${on ? "" : "text-white/40"}`}>{c.count}</span>
            </button>
          );
        })}
      </div>

      {list.length === 0 ? (
        <div className="p-10 border border-dashed border-white/10 text-center text-white/40">
          {tab === "public"
            ? "No public templates yet — be the first to publish one."
            : "You haven't saved any templates yet."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((t) => (
            <TemplateCard
              key={t.id}
              t={t}
              scope={tab}
              query={search}
              onChanged={loadAll}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateCard({ t, scope, onChanged, query = "" }) {
  const cat = RULE_INPUTS[t.input] || RULE_INPUTS.weather;
  const Icon = cat.icon;
  const action = RULE_ACTIONS[t.action] || RULE_ACTIONS.show;
  const [copied, setCopied] = useState(false);

  const copyJson = async () => {
    const payload = {
      input: t.input,
      value: t.value,
      action: t.action,
      creative: t.creative,
    };
    // Clipboard may reject in headless contexts / without permission — but
    // staging the rule for import + bumping uses_count must still run.
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    } catch {
      // ignore — the important path is the localStorage stage below
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    // Pre-stage in localStorage so RuleBuilder can pull it in later
    try {
      localStorage.setItem(
        "mirrorloops_pending_rule_import",
        JSON.stringify(payload),
      );
    } catch {}
    toast.success("Rule staged — open a campaign's Rules to insert it");
    try {
      await api.post(`/ads/rule-templates/${t.id}/use`);
    } catch {}
    onChanged();
  };

  const togglePublic = async () => {
    try {
      await api.patch(`/ads/rule-templates/${t.id}`, {
        is_public: !t.is_public,
      });
      toast.success(t.is_public ? "Unpublished" : "Published to marketplace");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/ads/rule-templates/${t.id}`);
      toast.success("Template deleted");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const fork = async () => {
    try {
      await api.post(`/ads/rule-templates/${t.id}/fork`);
      toast.success(`Forked to your private templates`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div
      className="border border-white/10 bg-white/[0.02] p-5 flex flex-col hover:border-[#D4B878]/40 transition"
      data-testid={`rt-card-${t.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={16} style={{ color: cat.color }} />
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
            {cat.label}
          </span>
          {t.category && (
            <span
              data-testid={`rt-card-category-${t.id}`}
              className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 border"
              style={{
                color: (CATEGORY_META[t.category] || CATEGORY_META.general).color,
                borderColor:
                  (CATEGORY_META[t.category] || CATEGORY_META.general).color +
                  "55",
              }}
            >
              {(CATEGORY_META[t.category] || CATEGORY_META.general).label}
            </span>
          )}
        </div>
        {t.uses_count > 0 && (
          <div className="text-[10px] text-[#E8D6A3] flex items-center gap-1 font-mono">
            <TrendingUp size={11} /> {t.uses_count}
          </div>
        )}
      </div>
      {typeof t.fires_in_country === "number" && t.fires_in_country > 0 && (
        <div
          data-testid={`rule-country-fires-${t.id}`}
          className="mb-2 inline-flex items-center gap-1.5 text-[10px] font-mono text-[#CCFF00] bg-[#CCFF00]/[0.06] border border-[#CCFF00]/25 rounded-full px-2 py-0.5 self-start"
        >
          <Flame size={10} /> {t.fires_in_country} fires here (30d)
        </div>
      )}
      <div className="font-bold text-lg text-white mb-1">
        <Highlight text={t.name} query={query} />
      </div>
      {t.forked_from_name && (
        <div
          data-testid={`rt-forked-from-${t.id}`}
          className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1 flex items-center gap-1"
        >
          <GitFork size={10} />
          forked from{" "}
          <span className="text-white/70">
            {t.forked_from_owner_name || "Mirrorloops"}
          </span>{" "}
          ·{" "}
          <span className="text-[#E8D6A3] normal-case tracking-normal">
            {t.forked_from_name}
          </span>
        </div>
      )}
      {t.description && (
        <div className="text-xs text-white/50 line-clamp-2">
          <Highlight text={t.description} query={query} />
        </div>
      )}
      <div className="mt-3 p-2 bg-black border border-white/10 font-mono text-[11px] text-white/70">
        <span className="text-white/40">WHEN</span> {t.input}={" "}
        <span className="text-[#E8D6A3]">{t.value}</span>{" "}
        <span className="text-white/40">THEN</span>{" "}
        <span className={action.color}>{action.label}</span>
        {t.creative ? <span className="text-white/60"> · {t.creative}</span> : null}
      </div>

      <LiveSimulator rule={t} />

      <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-white/40">
        <span className="flex items-center gap-1">
          {t.owner_id ? <User size={11} /> : <Store size={11} />}
          {t.owner_name || "Mirrorloops"}
        </span>
        {scope === "mine" && (
          <span
            className={`px-1.5 py-0.5 border ${
              t.is_public
                ? "border-emerald-500/30 text-emerald-300"
                : "border-white/10 text-white/40"
            }`}
          >
            {t.is_public ? "Public" : "Private"}
          </span>
        )}
      </div>

      <div className="mt-4 flex gap-2">
        <Button
          onClick={copyJson}
          size="sm"
          data-testid={`rt-use-${t.id}`}
          className="flex-1 bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full text-xs"
        >
          {copied ? <Check size={12} className="mr-1" /> : <Copy size={12} className="mr-1" />}
          Use
        </Button>
        {scope === "public" && (
          <Button
            onClick={fork}
            variant="outline"
            size="sm"
            data-testid={`rt-fork-${t.id}`}
            className="bg-white/[0.03] border-white/15 hover:border-white text-xs"
          >
            <GitFork size={12} className="mr-1" /> Fork
          </Button>
        )}
        {scope === "mine" && (
          <>
            <HistoryButton t={t} />
            <TestEnvsButton t={t} />
            <Button
              onClick={togglePublic}
              variant="outline"
              size="sm"
              data-testid={`rt-toggle-${t.id}`}
              className="bg-white/[0.03] border-white/15"
            >
              <Share2 size={12} />
            </Button>
            <Button
              onClick={remove}
              variant="outline"
              size="sm"
              data-testid={`rt-delete-${t.id}`}
              className="bg-white/[0.03] border-white/15 hover:border-red-400 hover:text-red-400"
            >
              <Trash2 size={12} />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * LiveSimulator — a 3-second per-card preview. Cycles through 5 environments
 * (~600ms each) and shows a bright FIRING halo whenever the current env
 * value matches the rule's `value`. Auto-pauses on hover so the buyer can
 * inspect the current environment.
 */
function LiveSimulator({ rule }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % SIM_ENVS.length), 600);
    return () => clearInterval(t);
  }, [paused]);

  const env = SIM_ENVS[idx];
  const currentValue = env[rule.input];
  const firing = currentValue === rule.value;
  const action = RULE_ACTIONS[rule.action] || RULE_ACTIONS.show;

  return (
    <div
      className="mt-3"
      data-testid={`rt-simulator-${rule.id}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="flex items-center justify-between text-[9px] uppercase tracking-[0.22em] text-white/40 mb-1">
        <span className="flex items-center gap-1">
          <Zap size={9} className="text-[#00E5FF]" /> Live sim
        </span>
        <span className={firing ? "text-[#E8D6A3]" : "text-white/30"}>
          {firing ? "● firing" : "● idle"}
        </span>
      </div>
      <div
        className={`p-2 border font-mono text-[10px] transition ${
          firing
            ? "border-[#E8D6A3] bg-gradient-to-r from-[#D4B878]/20 to-transparent"
            : "border-white/10 bg-white/[0.02]"
        }`}
      >
        <div className="text-white/40 mb-1">
          env · <span className="text-white/70">{rule.input}</span> ={" "}
          <span
            className={firing ? "text-[#E8D6A3]" : "text-white/60"}
          >
            {currentValue}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className={`h-1 flex-1 overflow-hidden bg-white/[0.06]`}
          >
            <div
              className={`h-full transition-all duration-500 ${
                firing ? "bg-[#E8D6A3]" : "bg-white/20"
              }`}
              style={{ width: `${((idx + 1) / SIM_ENVS.length) * 100}%` }}
            />
          </div>
          {firing && (
            <span
              className={`text-[9px] font-bold ${action.color}`}
              data-testid={`rt-simulator-firing-${rule.id}`}
            >
              → {action.label}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}




function NewTemplateDialog({ open, setOpen, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    description: "",
    input: "weather",
    value: RULE_INPUTS.weather.options[0],
    action: "show",
    creative: "",
    is_public: false,
    category: "general",
  });
  const [busy, setBusy] = useState(false);

  const cat = RULE_INPUTS[form.input];

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Give the template a short name");
      return;
    }
    setBusy(true);
    try {
      await api.post("/ads/rule-templates", form);
      toast.success("Template saved");
      setOpen(false);
      onCreated();
      setForm({
        name: "",
        description: "",
        input: "weather",
        value: RULE_INPUTS.weather.options[0],
        action: "show",
        creative: "",
        is_public: false,
        category: "general",
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="rt-new-btn"
          className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
        >
          <Plus size={14} className="mr-2" /> Save a new template
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#14110E] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={18} className="text-[#E8D6A3]" /> New rule template
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-1 block">
              Name
            </Label>
            <Input
              data-testid="rt-new-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Rainy afternoon boost"
              className="bg-white/[0.02] border-white/10"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-1 block">
              Description (optional)
            </Label>
            <Textarea
              data-testid="rt-new-description"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              placeholder="What situation is this rule great for?"
              className="bg-white/[0.02] border-white/10 min-h-[60px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-1 block">
                Input
              </Label>
              <Select
                value={form.input}
                onValueChange={(v) =>
                  setForm({
                    ...form,
                    input: v,
                    value: RULE_INPUTS[v].options[0],
                  })
                }
              >
                <SelectTrigger
                  data-testid="rt-new-input"
                  className="bg-white/[0.02] border-white/10 text-white"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#14110E] border-white/10 text-white">
                  {Object.entries(RULE_INPUTS).map(([k, c]) => (
                    <SelectItem key={k} value={k}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-1 block">
                Value
              </Label>
              <Select
                value={form.value}
                onValueChange={(v) => setForm({ ...form, value: v })}
              >
                <SelectTrigger
                  data-testid="rt-new-value"
                  className="bg-white/[0.02] border-white/10 text-white"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#14110E] border-white/10 text-white">
                  {cat.options.map((o) => (
                    <SelectItem key={o} value={o}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-1 block">
                Action
              </Label>
              <Select
                value={form.action}
                onValueChange={(v) => setForm({ ...form, action: v })}
              >
                <SelectTrigger
                  data-testid="rt-new-action"
                  className="bg-white/[0.02] border-white/10 text-white"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#14110E] border-white/10 text-white">
                  {Object.entries(RULE_ACTIONS).map(([k, a]) => (
                    <SelectItem key={k} value={k}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-1 block">
                Creative / boost
              </Label>
              <Input
                data-testid="rt-new-creative"
                value={form.creative}
                onChange={(e) =>
                  setForm({ ...form, creative: e.target.value })
                }
                placeholder="e.g. 1.5x or creative name"
                className="bg-white/[0.02] border-white/10"
              />
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-1 block">
              Category
            </Label>
            <Select
              value={form.category}
              onValueChange={(v) => setForm({ ...form, category: v })}
            >
              <SelectTrigger
                data-testid="rt-new-category"
                className="bg-white/[0.02] border-white/10 text-white"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#14110E] border-white/10 text-white">
                {Object.entries(CATEGORY_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
            <input
              type="checkbox"
              data-testid="rt-new-public"
              checked={form.is_public}
              onChange={(e) =>
                setForm({ ...form, is_public: e.target.checked })
              }
              className="accent-[#D4B878]"
            />
            Publish to the public marketplace
          </label>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            className="text-white/60"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="rt-new-submit"
            className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
          >
            {busy ? "Saving…" : "Save template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * TestEnvsButton — private-only. Save named test envs (weather/time/event/
 * inventory/venue combinations) so you can replay them against any future
 * version of this template and confirm the rule still fires.
 *
 * Live status inside the dialog runs the CURRENT template state against each
 * saved env: green = firing, amber = idle. Cap: 10 envs per template.
 */
function TestEnvsButton({ t }) {
  const [open, setOpen] = useState(false);
  const [envs, setEnvs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [draft, setDraft] = useState({});

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/ads/rule-templates/${t.id}/test-envs`);
      setEnvs(r.data.envs || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) load();
    if (!v) {
      setName("");
      setDraft({});
    }
  };

  const save = async () => {
    const clean = Object.fromEntries(
      Object.entries(draft).filter(([, v]) => v && String(v).trim()),
    );
    if (!name.trim() || Object.keys(clean).length === 0) {
      toast.error("Give the env a name and at least one input");
      return;
    }
    try {
      await api.post(`/ads/rule-templates/${t.id}/test-envs`, {
        name: name.trim(),
        env: clean,
      });
      toast.success("Env saved");
      setName("");
      setDraft({});
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/ads/rule-templates/${t.id}/test-envs/${id}`);
      toast.success("Env removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Rule fires when the current template value matches the env value for the
  // template's own input axis (e.g. rule.input=weather + env.weather=rain).
  const fires = (env) =>
    env && env[t.input] != null &&
    String(env[t.input]).toLowerCase() === String(t.value || "").toLowerCase();

  const count = envs.length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid={`rt-envs-${t.id}`}
          title={count ? `${count} saved test env${count === 1 ? "" : "s"}` : "No saved envs yet"}
          className="bg-white/[0.03] border-white/15 hover:border-white relative"
        >
          <FlaskConical size={12} />
          {count > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#CCFF00] text-black text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {count}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#14110E] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical size={18} className="text-[#CCFF00]" /> Saved test envs
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500 mt-1">
            Snapshot a scenario for{" "}
            <span className="text-white font-semibold">{t.name}</span> and replay
            it against future edits. Envs are private and capped at 10.
          </DialogDescription>
        </DialogHeader>

        {busy && <div className="text-white/50 text-sm py-4">Loading…</div>}
        {!busy && (
          <>
            <div
              data-testid={`rt-envs-list-${t.id}`}
              className="divide-y divide-white/10 border border-white/10"
            >
              {envs.length === 0 ? (
                <div className="p-4 text-white/40 text-sm">
                  No saved envs yet. Add one below to replay a scenario later.
                </div>
              ) : (
                envs.map((e) => {
                  const on = fires(e.env);
                  return (
                    <div
                      key={e.id}
                      data-testid={`rt-env-row-${e.id}`}
                      className="p-3 flex items-start gap-3"
                    >
                      <span
                        className={`mt-1 w-2 h-2 rounded-full shrink-0 ${
                          on ? "bg-[#CCFF00]" : "bg-white/20"
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-white truncate">
                            {e.name}
                          </div>
                          <span
                            data-testid={`rt-env-status-${e.id}`}
                            className={`text-[9px] uppercase tracking-[0.18em] font-semibold ${
                              on ? "text-[#CCFF00]" : "text-white/40"
                            }`}
                          >
                            {on ? "firing" : "idle"}
                          </span>
                        </div>
                        <div className="text-[10px] font-mono text-white/50 truncate">
                          {Object.entries(e.env)
                            .map(([k, v]) => `${k}=${v}`)
                            .join(" · ")}
                        </div>
                      </div>
                      <button
                        onClick={() => remove(e.id)}
                        data-testid={`rt-env-delete-${e.id}`}
                        className="text-white/30 hover:text-red-400 shrink-0"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            <div className="mt-4 space-y-3 border border-white/10 p-3 bg-white/[0.02]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                New env
              </div>
              <Input
                data-testid={`rt-env-name-${t.id}`}
                value={name}
                onChange={(ev) => setName(ev.target.value)}
                placeholder="e.g. Rainy Monday morning"
                className="bg-transparent border-white/15 text-sm"
              />
              <div className="grid grid-cols-2 gap-2">
                {Object.keys(RULE_INPUTS).map((k) => (
                  <div key={k}>
                    <div className="text-[9px] uppercase tracking-[0.18em] text-white/40 mb-1">
                      {k}
                    </div>
                    <Input
                      data-testid={`rt-env-input-${t.id}-${k}`}
                      value={draft[k] || ""}
                      onChange={(ev) =>
                        setDraft({ ...draft, [k]: ev.target.value })
                      }
                      placeholder={
                        k === t.input ? `try "${t.value}"` : "—"
                      }
                      className="bg-transparent border-white/10 text-xs h-8"
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={save}
                  size="sm"
                  data-testid={`rt-env-save-${t.id}`}
                  disabled={envs.length >= 10}
                  className="bg-[#CCFF00] hover:bg-[#E8FF66] text-black rounded-full disabled:opacity-40"
                >
                  Save env
                </Button>
              </div>
              {envs.length >= 10 && (
                <div className="text-[10px] text-amber-400">
                  Max 10 envs per template — delete one to add another.
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}




function HistoryButton({ t }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [envs, setEnvs] = useState([]); // owner-only test envs for replay
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [diffAgainst, setDiffAgainst] = useState(null); // {a,b} versions
  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/ads/rule-templates/${t.id}/history`);
      setHistory(r.data);
      // Best-effort: envs endpoint 403s for non-owners; swallow silently.
      try {
        const e = await api.get(`/ads/rule-templates/${t.id}/test-envs`);
        setEnvs(e.data.envs || []);
      } catch {
        setEnvs([]);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const forkVersion = async (v) => {
    try {
      await api.post(`/ads/rule-templates/${t.id}/fork?version=${v}`);
      toast.success(`Forked v${v} to your templates`);
      setHistory(null); // invalidate — next open refetches fork_counts
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const bulkFork = async () => {
    if (!selected.size) return;
    try {
      const r = await api.post(`/ads/rule-templates/${t.id}/bulk-fork`, {
        versions: Array.from(selected),
      });
      const err = (r.data.errors || []).length;
      toast.success(
        `Forked ${r.data.count} version${r.data.count === 1 ? "" : "s"}${
          err ? ` (${err} failed)` : ""
        }`,
      );
      setSelected(new Set());
      setHistory(null); // invalidate — next open refetches fork_counts
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleSelect = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    setSelected(next);
  };

  const openDiff = (a, b) => setDiffAgainst({ a, b });

  const onOpenChange = (v) => {
    setOpen(v);
    if (v && !history) load();
    if (!v) {
      setSelected(new Set());
      setDiffAgainst(null);
    }
  };

  const count = (t.versions || []).length;

  // "current" snapshot for diff comparison (never in versions list)
  const currentSnap = history?.current;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid={`rt-history-${t.id}`}
          title={count ? `${count} prior version${count === 1 ? "" : "s"}` : "No prior versions yet"}
          className="bg-white/[0.03] border-white/15 hover:border-white relative"
        >
          <History size={12} />
          {count > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#D4B878] text-black text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {count}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#14110E] border-white/10 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History size={18} className="text-[#E8D6A3]" /> Version history
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500 mt-1">
            Last 5 revisions of{" "}
            <span className="text-white font-semibold">{t.name}</span>. Diff
            any row against the current version, or select several to fork as
            a batch.
          </DialogDescription>
        </DialogHeader>

        {busy && <div className="text-white/50 text-sm py-4">Loading…</div>}
        {history && !diffAgainst && (
          <>
            <div
              data-testid={`rt-history-list-${t.id}`}
              className="divide-y divide-white/10 border border-white/10"
            >
              {history.versions.length === 0 ? (
                <div className="p-4 text-white/40 text-sm">
                  No prior versions yet — edit this template to create the
                  first snapshot.
                </div>
              ) : (
                history.versions.map((v) => {
                  const forkCount = history.fork_counts?.[String(v.version)] || 0;
                  return (
                  <div
                    key={v.version}
                    data-testid={`rt-history-row-${v.version}`}
                    className="p-3 flex items-start gap-3"
                  >
                    <input
                      type="checkbox"
                      data-testid={`rt-history-select-${v.version}`}
                      checked={selected.has(v.version)}
                      onChange={() => toggleSelect(v.version)}
                      className="mt-1 accent-[#D4B878]"
                    />
                    <span className="mt-0.5 text-[10px] font-mono text-[#E8D6A3] w-8">
                      v{v.version}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-medium text-white truncate">
                          {v.name}
                        </div>
                        {forkCount > 0 && (
                          <span
                            data-testid={`rt-history-forks-${v.version}`}
                            title={`${forkCount} ${forkCount === 1 ? "person has" : "people have"} forked this version`}
                            className="inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-[#CCFF00] bg-[#CCFF00]/10 border border-[#CCFF00]/30 rounded-full px-1.5 py-0.5"
                          >
                            <TrendingUp size={9} />
                            {forkCount} {forkCount === 1 ? "fork" : "forks"}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-white/40 font-mono">
                        when {v.input}={v.value} → {v.action}
                        {v.creative ? ` · ${v.creative}` : ""}
                      </div>
                      {v.description && (
                        <div className="text-[11px] text-white/50 truncate mt-1">
                          {v.description}
                        </div>
                      )}
                      <div className="text-[9px] text-white/30 mt-1 flex items-center gap-1">
                        <Clock size={9} />
                        {new Date(v.saved_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button
                        onClick={() => openDiff(v, currentSnap)}
                        size="sm"
                        variant="outline"
                        data-testid={`rt-history-diff-${v.version}`}
                        className="bg-white/[0.03] border border-white/15 hover:border-white text-xs"
                      >
                        Diff
                      </Button>
                      <Button
                        onClick={() => forkVersion(v.version)}
                        size="sm"
                        data-testid={`rt-history-fork-${v.version}`}
                        className="bg-white/[0.03] border border-white/15 hover:border-white text-xs"
                      >
                        <GitFork size={11} className="mr-1" /> Fork
                      </Button>
                    </div>
                  </div>
                  );
                })
              )}
            </div>

            {history.versions.length > 0 && (
              <div className="mt-3 space-y-2">
                {selected.size > 0 && (
                  <div
                    data-testid="rt-history-bulk-preview"
                    className="border border-[#D4B878]/30 bg-[#D4B878]/[0.06] rounded p-2 space-y-1"
                  >
                    <div className="text-[9px] uppercase tracking-[0.22em] text-[#E8D6A3]">
                      Will create {selected.size} clone{selected.size === 1 ? "" : "s"}
                    </div>
                    <ul className="space-y-0.5 max-h-24 overflow-auto pr-1">
                      {Array.from(selected)
                        .sort((a, b) => a - b)
                        .map((ver) => {
                          const snap =
                            history.versions.find((x) => x.version === ver) || {};
                          const raw = `${snap.name || t.name} (fork · v${ver})`;
                          const cloneName =
                            raw.length > 80 ? raw.slice(0, 80) : raw;
                          return (
                            <li
                              key={ver}
                              data-testid={`rt-history-bulk-preview-${ver}`}
                              className="text-[11px] font-mono text-white/80 truncate flex items-center gap-1.5"
                            >
                              <GitFork size={10} className="text-[#D4B878] shrink-0" />
                              {cloneName}
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                    {selected.size} of {history.versions.length} selected
                  </div>
                  <Button
                    onClick={bulkFork}
                    disabled={selected.size === 0}
                    size="sm"
                    data-testid="rt-history-bulk-fork"
                    className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full disabled:opacity-40"
                  >
                    <GitFork size={11} className="mr-1" /> Fork {selected.size || ""} selected
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {diffAgainst && (
          <DiffPanel
            older={diffAgainst.a}
            newer={diffAgainst.b}
            envs={envs}
            onBack={() => setDiffAgainst(null)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

